/**
 * Credit System — TypeScript wire contract (user endpoints).
 *
 * Source of truth: backend handoff pack `docs/handoffs/credit_system/api_contract.md`
 * (repo: orphex_django_rag). The block under "Shared / verbatim" is copied verbatim from
 * api_contract.md §"TypeScript types"; the response shapes below it are typed from the
 * per-endpoint JSON fixtures in the same file. Contract 11 §5 keeps this file as-is —
 * it is the wire, not a design surface, so it is ported, never rewritten.
 *
 * The admin-panel shapes this file used to split off into `./credit.admin.types` were removed
 * with the admin panel itself — it now lives entirely in a separate project.
 *
 * Conventions:
 * - User endpoints use the {status,data,message} success envelope (ApiSuccess<T>).
 * - Credits are always integers.
 */

/* ────────────────────────────────────────────────────────────────────────────
 * Shared (verbatim from api_contract.md)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * §2.16 (ORP-17970) — a single add-on grant ("lot"): its own validity window + remaining.
 * An account may hold several active lots at once; they are consumed soonest-expiry-first
 * and each burns on its own date (NOT at the cycle reset).
 */
export interface AddonLot {
  amount: number
  remaining: number
  started_at: string | null // the lot's own validity window
  expires_at: string | null
  source: string // "admin" | "stripe"
  package: string | null
}

export interface BalancePayload {
  remaining: number // spendable now (trial+addon+base, trial only if active)
  total: number // plan_credits + active add-on ceiling (full cycle ceiling)
  plan_credits: number // monthly grant for the account's plan
  plan: string | null // plan display name (active AI package; "Custom" for a custom assignment), null if none
  start_date: string | null // ISO 8601; current cycle start (period start)
  reset_date: string | null // ISO 8601; period end — when base re-grants (add-ons are NOT cycle-bound)
  trial_expires_at: string | null // ISO 8601; null if no trial
  trial_days_remaining: number | null // whole days left, 0 if expired, null if no trial
  buckets: { trial: number; addon: number; base: number }
  // §2.16 (ORP-17970): add-on is a standalone timed product — a list of lots, each with its
  // own validity. `remaining` mirrors buckets.addon; `expires_at` is the soonest-expiring
  // active lot (NOT the cycle reset; null when no add-on).
  addon: { total: number; remaining: number; expires_at: string | null; lots: AddonLot[] }
  spend_by_feature: Record<string, number> // current cycle, credits spent per feature_key
  locked: boolean // true when remaining <= 0  → render locked UI
}

export interface ApiSuccess<T> {
  status: 'success'
  data: T
  message: string
}

/**
 * The credit system's machine error envelope (402 CREDIT_DEPLETED / 403 PACKAGE_FORBIDDEN),
 * distinct from the {status,data,message} success envelope above.
 *
 * Named `ApiError` in v3; renamed here because `shared/http` exports an `ApiError` CLASS
 * (module 03) that every layer above `api/` imports — two different things under one name in
 * the same file is the clash contract 11 §5 calls out. `service/creditError` (ORP-18683)
 * reads this shape off `ApiError.details`.
 */
export interface CreditErrorEnvelope {
  error: { code: string; message: string; user_visible: boolean; details?: unknown }
}

// Causal run credits (embedded in execution result)
export interface CausalCredits {
  total: number
  charged: number
  locked: number
  per_profile: Record<string, { charged: number; locked: number }>
}

export const ADDON_PRESETS = [50, 100, 200, 300] as const // gap G6 fallback (also via plans-config)
export const CREDIT_ERROR_CODES = {
  DEPLETED: 'CREDIT_DEPLETED',
  FORBIDDEN: 'PACKAGE_FORBIDDEN',
} as const
export const DEFAULT_TRIAL_CREDITS = 300 // free trial grant; change here to propagate everywhere

/* ────────────────────────────────────────────────────────────────────────────
 * Response shapes (typed from the per-endpoint fixtures in api_contract.md)
 * ──────────────────────────────────────────────────────────────────────────── */

/** §1.1 GET …/credits/balance/ */
export type BalanceResponse = ApiSuccess<BalancePayload>

/** Config-mode preview: the backend resolves the run's creative count from its scope. */
export interface CreativePreviewConfigParams {
  creative_type: 'image' | 'video'
  date_from: string
  date_to: string
  workspace_id: number
  filters?: unknown
}

/** Legacy `getCreativePreview` count mode: Creative AI prices the part of a stopped run the
 *  balance still covers. */
export interface CreativePreviewCountsParams {
  images: number
  videos: number
  workspaceId: number
}

/** §1.5 GET …/credits/creative-preview/ — read-only, cap-aware cost preview. */
export interface CreativePreview {
  cost: number // chargeable*rate (image 1 / video 3)
  scope: { images: number; videos: number }
  remaining: number
  affordable: boolean
  affordable_subset: { images: number; videos: number } // greedy, images before videos
  // FE-W4 config mode: BE resolves the run's creative count server-side (allow-list minus
  // already-labelled). `chargeable` = what the run actually charges; `cost` prices it.
  config?: {
    creative_type: 'image' | 'video'
    total: number
    already_labeled: number
    chargeable: number
  }
  analysis?: {
    cost: number
    affordable: boolean
    rule_count: number
    labels: number
    assets: number
    per_rule: number
  }
}
export type CreativePreviewResponse = ApiSuccess<CreativePreview>

export interface RelabelPreview {
  cost: number
  remaining: number
  affordable: boolean
  scope: { images: number; videos: number }
  affordable_subset: { images: number; videos: number }
  relabel: { creative_type: 'image' | 'video'; count: number }
  analysis?: { analyses: number; cost: number; affordable: boolean } | null
}
export type RelabelPreviewResponse = ApiSuccess<RelabelPreview>

/* ────────────────────────────────────────────────────────────────────────────
 * Credit history (§1.2)
 * ──────────────────────────────────────────────────────────────────────────── */

export interface HistoryEntry {
  id: number
  created_at: string | null
  entry_type: 'grant' | 'consume' | 'reset' | 'expire' | 'adjust'
  bucket: 'trial' | 'addon' | 'base' | null // grant/expire only
  delta: number // positive = added, negative = spent
  balance_after: number
  feature_key: string | null // 'orpheus.chat' | 'causal.impact' | 'creative_ai.label' — consume only
  source: string | null
}
export interface HistoryPayload {
  entries: HistoryEntry[]
  count: number // total records (for pagination)
  limit: number
  offset: number
}
export type HistoryResponse = ApiSuccess<HistoryPayload>

/* ────────────────────────────────────────────────────────────────────────────
 * Feature keys (open map — render any key; unknown → "Other")
 * ──────────────────────────────────────────────────────────────────────────── */

export const FEATURE_KEYS = {
  ORPHEUS: 'orpheus.chat',
  IMPACT: 'causal.impact',
  CREATIVE: 'creative_ai.label',
} as const

export type FeatureKey = (typeof FEATURE_KEYS)[keyof typeof FEATURE_KEYS]
